import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../config/apiUrl';

const BASE_URL = getApiBaseUrl();

const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
const COLORS = ['', '#EF4444', '#F97316', '#EAB308', '#22C55E', '#059669'];

export default function CsatFeedbackPage({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qRating = parseInt(params.get('rating') || '0', 10);
    if (qRating >= 1 && qRating <= 5) setRating(qRating);

    fetch(`${BASE_URL}/csat/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Invalid feedback link');
        if (data.alreadySubmitted) {
          setAlreadySubmitted(true);
          setTicket(data);
          setRating(data.rating || 0);
        } else {
          setTicket(data);
        }
      })
      .catch((err) => setError(err.message || 'Could not load feedback form'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (!rating) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${BASE_URL}/csat/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, remarks: remarks.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save feedback');
      setDone(true);
    } catch (err) {
      setError(err.message || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>Loading feedback form…</div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#DC2626', marginBottom: 8 }}>Feedback unavailable</div>
          <p style={{ color: '#6B7280', margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (done || alreadySubmitted) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Thank you!</div>
          <p style={{ color: '#6B7280', margin: 0 }}>
            Your feedback for ticket <strong>{ticket?.ticketNumber}</strong> has been recorded.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: 14, color: '#16A34A', fontWeight: 700, marginBottom: 8 }}>PowerDesk / SYSBER</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#1A202C', marginBottom: 6 }}>Rate your support experience</div>
        <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 24px' }}>
          Ticket <strong>{ticket?.ticketNumber}</strong>
          {ticket?.subject ? ` — ${ticket.subject}` : ''}
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHovered(n)}
              onMouseLeave={() => setHovered(0)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 38,
                color: n <= (hovered || rating) ? COLORS[hovered || rating] : '#D1D5DB',
                lineHeight: 1,
              }}
            >
              ★
            </button>
          ))}
        </div>
        {(hovered || rating) > 0 && (
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS[hovered || rating], marginBottom: 16 }}>
            {LABELS[hovered || rating]}
          </div>
        )}

        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Additional comments (optional)"
          rows={4}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1.5px solid #E2E8F0',
            borderRadius: 8,
            fontSize: 14,
            resize: 'none',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
            marginBottom: 16,
          }}
        />

        {error && (
          <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!rating || saving}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: 'none',
            borderRadius: 10,
            background: rating ? '#16A34A' : '#E2E8F0',
            color: rating ? '#fff' : '#9CA3AF',
            fontSize: 15,
            fontWeight: 700,
            cursor: rating ? 'pointer' : 'default',
          }}
        >
          {saving ? 'Submitting…' : 'Submit feedback'}
        </button>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
};

const cardStyle = {
  background: '#fff',
  borderRadius: 20,
  width: '100%',
  maxWidth: 440,
  padding: '32px 28px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
  textAlign: 'center',
};
